import { useField } from "formik";
import React, { useRef } from "react";
import { AsyncSearchFieldConfigNew, FormFieldOption } from "../types";
import { useMutation } from "react-query";
import debounce from "lodash/debounce";
import { DEBOUNCE_TIME } from "shared/utils/constants";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import { defaultOptionsMapper } from "./utils";
import { toNameKey } from "shared/utils";
import "@reach/combobox/styles.css";
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from "@reach/combobox";
import ErrorField from "../errorField";
import classNames from "classnames";
import Variant from "shared/enums/Variant";
import Size from "shared/enums/Size";
import { useFormContextData } from "shared/form/context";
import { getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";

function AsyncSearchField(props: AsyncSearchFieldConfigNew) {
  const formContextData = useFormContextData();
  const optionSelectedRef = React.useRef<FormFieldOption | undefined>(props.alreadySelectedOption);
  const [field, meta, fieldHelper] = useField({
    name: props.name,
    validate: props.validate
  });
  const elementRef = useRef<any>();
  const { mutateAsync: getData, data: apiData = [] } = useMutation(props.apiWrapper, {
    meta: { noLoader: props.hideLoader }
  });
  const data: Array<any> = apiData as Array<any>;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMutate: Function = React.useCallback(debounce(getData, DEBOUNCE_TIME), []);

  //   check if all conditions are met and if yes then make api call
  const getDataFromApi = React.useCallback(
    (value: string) => {
      const fieldValue = props.convertUsingNameKey ? toNameKey(value) : value?.trim();
      const canGetData = fieldValue.length > 1 && !props.disabled;
      if (canGetData) {
        const params = props.getRequestBody ? props.getRequestBody(fieldValue) : fieldValue?.trim();
        debouncedMutate(params);
      }
    },
    [debouncedMutate, props]
  );

  React.useEffect(() => {
    // to avoid reset and open dropdown on focus when the org name is already present
    getDataFromApi(field.value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = React.useMemo(() => {
    return data.map(obj => (props.optionsMapper ? props.optionsMapper(obj) : defaultOptionsMapper(obj)));
  }, [data, props]);

  // to be called whenever a value is selected
  //   currently called when:
  //        1. option is selected from dropdown
  //        2. on typing and exact match is found

  const optionSelectedHandler = React.useCallback(
    async (selected: string) => {
      const obj = options.find(item => item.label === selected);
      await fieldHelper.setValue(obj.label);
      optionSelectedRef.current = obj;
      props.optionSelectedCallback?.(obj, data);
      // reset();
    },
    [data, fieldHelper, props, options]
  );

  // update the field value and check if we can find match in data available or make new api call
  const changeHandler = React.useCallback(
    async e => {
      const value = e.target.value;

      //   update the value to reflect in search field
      await fieldHelper.setValue(value);
      if (!meta.touched) {
        await fieldHelper.setTouched(true);
      }
      // find if any exact match present from existing options

      const optionMatched = options.find(opt => opt.label.toLowerCase() === value.trim().toLowerCase());

      if (optionMatched) {
        //   if a match is found then we can use that directly and don't need to call the api
        optionSelectedHandler(optionMatched.label);
        // reset(); // called to hide options
        optionSelectedRef.current = optionMatched;

        // todo:rr cancel api request if pending else api call will be made that was triggered earlier
        return;
      }
      // optionSelectedRef.current as first condition is intentional
      //      else reset will be called even if there was no previously selected option
      if (
        optionSelectedRef.current &&
        typeof optionSelectedRef.current.label === "string" &&
        optionSelectedRef.current.label.toLowerCase() !== value.trim().toLowerCase()
      ) {
        optionSelectedRef.current = undefined;
        //   if value is changed and we previously had selected option then in essense the option is now unselected
        props.resetCallback?.();
      }
      //   if we don't find a match in existing data then make api call
      getDataFromApi(value);
    },
    [fieldHelper, getDataFromApi, meta.touched, optionSelectedHandler, options, props]
  );

  const blurHandler = React.useCallback(
    async e => {
      if (props.doNotRunOnBlurRef?.current) return;
      await fieldHelper.setTouched(true, false);
    },
    [props.doNotRunOnBlurRef, fieldHelper]
  );

  const fieldValue = props.convertUsingNameKey ? toNameKey(field.value) : field.value?.trim();

  const computedClassNames = React.useMemo(
    () => getClassNames(props.className, props.variant, props.size, meta.touched && meta.error),
    [props.className, props.variant, props.size, meta.touched, meta.error]
  );

  return (
    <FormGroup fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel
        label={props.label}
        htmlFor={field.name}
        required={props.required}
        additionalLabel={props.additionalLabel}
        infoLabel={props.infoLabel}
      />
      {props.additionalMsg ? props.additionalMsg : null}
      <div ref={elementRef} />
      {formContextData.isReadOnly ? (
        getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.SINGLE_LINE_TEXTFIELD, value: field.value })
      ) : (
        <Combobox onSelect={optionSelectedHandler} openOnFocus className={computedClassNames}>
          <ComboboxInput
            placeholder={props.placeholder || ""}
            onChange={changeHandler}
            onBlur={blurHandler}
            value={field.value}
            disabled={props.disabled}
          />
          {!!options.length && !optionSelectedRef.current && fieldValue?.length > 1 && (
            <ComboboxPopover>
              <ComboboxList className="custom_asyncSearch_options_list">
                {options.map((obj, index) => (
                  <ComboboxOption id={obj.label} key={index} value={obj.label} />
                ))}
              </ComboboxList>
            </ComboboxPopover>
          )}
        </Combobox>
      )}

      <ErrorField meta={meta} />

      {/* <Input
        name={props.name}
        value={field.value}
        type={InputType.TEXT}
        onChange={changeHandler}
        disabled={props.disabled}
        className={props.className}
        autoFocus={props.autoFocus}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
        hasError={meta.touched && meta.error}
      />
      <ErrorField meta={meta} />
      {activeRef.current && (
        <DisplayOptions
          options={options}
          searchValue={field.value}
          optionsMapper={props.optionsMapper}
          optionSelectedHandler={optionSelectedHandler}
          blurHandler={blurHandler}
        />
      )} */}
    </FormGroup>
  );
}

export default AsyncSearchField;

// function DisplayOptions(props: DisplayOptionsType) {
//   const closeRef = React.useRef<HTMLDivElement>(null);
//   useOnOutSideClick({ node: closeRef.current, onOutSideClick: props.blurHandler });
//   if (!props.options.length) return null;
//   return (
//     <div className="custom_asyncSearch_options" ref={closeRef}>
//       <ul className="list-unstyled custom_asyncSearch_options_list">
//         {props.options.map(obj => (
//           <li
//             key={obj.value}
//             className="custom_asyncSearch_options_list_item"
//             onClick={() => props.optionSelectedHandler(obj)}>
//             {obj.label}
//           </li>
//         ))}
//       </ul>
//     </div>
//   );
// }

function getClassNames(className?: string, variant?: Variant, size?: Size, hasError?: boolean | string) {
  return classNames(className, {
    inputBox: true,
    [`inputBox--${variant}`]: variant,
    [`inputBox--${size}`]: size,
    [`inputBox--hasError`]: hasError
  });
}
